import * as api from '@gitbook/api-client';
import { isActiveStatus, type OrganizationBilling } from '@gitbook/app-database';

export const PRICING_LABEL = 'pr_2024';

export type PricingProductDefinition = {
    key: api.BillingProduct;
    title: string;
    description: string;
};

/**
 * The order matters: products should be sorted from lowest to highest.
 *
 * This is because we use indexes to determine whether we are upgrading or
 * downgrading, when switching from an product to another.
 */
export const PRICING_PRODUCTS: readonly PricingProductDefinition[] = [
    {
        key: api.BillingProduct.Free2024,
        title: 'Free',
        description: 'Perfect for individuals',
    },
    {
        key: api.BillingProduct.Community2024,
        title: 'Community',
        description:
            'Everything you need to get started on your own or with your teammates on your community project',
    },
    // Plus2024 is deprecated but we'll keep it here for existing Plus customers, until phased out.
    // The plans table and EOT flow will hide it as needed.
    {
        key: api.BillingProduct.Plus2024,
        title: 'Plus',
        description: 'For small teams that want to collaborate',
    },
    {
        key: api.BillingProduct.Pro2024,
        title: 'Pro',
        description: 'For teams building docs at scale',
    },
    {
        key: api.BillingProduct.Enterprise2024,
        title: 'Enterprise',
        description: 'For companies seeking enterprise-grade workflows, controls, integrations',
    },
    {
        key: api.BillingProduct.Internal,
        title: 'Internal',
        description: 'For internal use only - same access level as Enterprise',
    },
] as const;

const PRICING_PRODUCT_BY_KEY = PRICING_PRODUCTS.reduce<Record<string, PricingProductDefinition>>(
    (byKey, product) => {
        byKey[product.key] = product;
        return byKey;
    },
    {}
);

/**
 * Get the index of a product in the list of products.
 */
export function getProductIndex(product: api.BillingProduct): number {
    return PRICING_PRODUCTS.findIndex((def) => def.key === product);
}

/**
 * Get a specific product definition
 */
export function getProductDefinition(key: string): PricingProductDefinition | null {
    return PRICING_PRODUCT_BY_KEY[key] ?? null;
}

/**
 * Determines whether the given product key is internal.
 */
export function checkIsInternalProductKey(key: string): key is api.BillingProduct.Internal {
    return key === api.BillingProduct.Internal;
}

/**
 * Determines whether the given product key is a community plan.
 */
export function checkIsCommunityProductKey(key: string): key is api.BillingProduct.Community2024 {
    return key === api.BillingProduct.Community2024;
}

/**
 * Determines whether the given product key is a free plan.
 */
export function checkIsFreeProductKey(key: string): key is api.BillingProduct.Free2024 {
    return key === api.BillingProduct.Free2024;
}

/**
 * Determines whether the given product key is an enterprise plan.
 *
 * IMPORTANT:
 * Use `checkIsActiveUnrestrictedBilling` instead of this function
 * to verify billing.product. It ensures the billing record is active, as
 * Enterprise orgs with canceled subscriptions are effectively on Free.
 *
 * If you have an organization object returned by the API (api.Organization),
 * it's safe to use `checkIsEnterpriseProductKey(organization.plan)` because the API encoder
 * will not return an Enterprise plan if the subscription is not active.
 */
export function checkIsEnterpriseProductKey(key: string): key is api.BillingProduct.Enterprise2024 {
    return key === api.BillingProduct.Enterprise2024;
}

/**
 * Determines whether the given product key is an unrestricted product.
 * Unrestricted products have access to all features and sites but they are not self-serve.
 */
export function checkIsUnrestrictedProductKey(
    key: string
): key is
    | api.BillingProduct.Enterprise2024
    | api.BillingProduct.Community2024
    | api.BillingProduct.Internal {
    return (
        checkIsCommunityProductKey(key) ||
        checkIsEnterpriseProductKey(key) ||
        checkIsInternalProductKey(key)
    );
}

/**
 * Determines whether the given product key is driven by a subscription in Stripe or manually
 * managed. Free plans have no subscription to read from, and unrestricted plans are not
 * self-serve: their subscription is just a placeholder.
 */
export function checkIsSubscriptionDrivenProductKey(key: string): boolean {
    return !checkIsFreeProductKey(key) && !checkIsUnrestrictedProductKey(key);
}

/**
 * Determines whether the given billing is an active unrestricted org
 */
export function checkIsActiveUnrestrictedBilling(
    billing: OrganizationBilling
): billing is OrganizationBilling & {
    status: 'trialing' | 'active' | 'past_due';
    product:
        | api.BillingProduct.Enterprise2024
        | api.BillingProduct.Community2024
        | api.BillingProduct.Internal;
} {
    return isActiveStatus(billing.status) && checkIsUnrestrictedProductKey(billing.product);
}

/**
 * Check if the two given products and intervals are equivalent.
 */
export function areEquivalentProducts(
    from: {
        product: api.BillingProduct;
        interval: api.BillingInterval;
    },
    to: {
        product: api.BillingProduct;
        interval: api.BillingInterval;
    }
): boolean {
    if (from.product !== to.product) {
        return false;
    }
    if (checkIsFreeProductKey(from.product)) {
        return true;
    }
    return from.interval === to.interval;
}

/**
 * Get the product definition for the billing product.
 */
export function getBillingProduct(billing: OrganizationBilling) {
    if (isActiveStatus(billing.status)) {
        return billing.product;
    }

    // When the subscription is not active, the current plan is Free by default
    return api.BillingProduct.Free2024;
}

/**
 * Determines whether the given product is legacy.
 * Legacy products may be displayed to existing customers to self-serve out of them.
 * It's not possible to upgrade or downgrade to a legacy product.
 */
export function isLegacyProduct(plan: api.BillingProduct): boolean {
    return plan === api.BillingProduct.Plus2024;
}

/**
 * Determines whether the given product is a deprecated plan.
 */
export function checkIsLegacyProductKey(plan: api.BillingProduct): boolean {
    return isLegacyProduct(plan);
}
